import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { 
  CalendarIcon, 
  Clock, 
  Wrench, 
  Car, 
  Shield, 
  Snowflake, 
  CircleDot,
  CheckCircle,
  Camera,
  FileText,
  X,
  Image,
  Sun,
  Package,
  Search,
  AlertCircle
} from "lucide-react";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// Reusable Photo Upload Component
const PhotoUpload = ({ label, description, file, preview, onCapture, onRemove, inputRef, testId }) => {
  return (
    <div>
      <Label className="text-[#333333]">{label}</Label>
      <p className="text-xs text-[#888B8D] mb-2">{description}</p>
      <div className="mt-1">
        {!file && !preview ? (
          <div 
            className="border-2 border-dashed border-[#D9D9D6]  p-4 text-center hover:border-[#000000] transition-colors cursor-pointer"
            onClick={() => inputRef.current?.click()}
            data-testid={`${testId}-area`}
          >
            <Camera className="w-8 h-8 text-[#888B8D] mx-auto mb-2" />
            <p className="text-sm text-[#888B8D] mb-1">
              Foto aufnehmen
            </p>
            <p className="text-xs text-[#888B8D]">
              Tippen zum Fotografieren
            </p>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={onCapture}
              className="hidden"
              data-testid={`${testId}-input`}
            />
          </div>
        ) : (
          <div className="border border-[#D9D9D6]  p-3 bg-[#F5F5F5]">
            <div className="flex items-center gap-3">
              {preview ? (
                <img 
                  src={preview} 
                  alt="Vorschau" 
                  className="w-16 h-16 object-cover "
                />
              ) : (
                <div className="w-16 h-16 bg-[#000000]  flex items-center justify-center">
                  <Image className="w-6 h-6 text-white" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-black truncate">{file?.name || "Foto"}</p>
                <p className="text-xs text-[#888B8D]">
                  {file ? `${(file.size / 1024).toFixed(1)} KB` : "Aufgenommen"}
                </p>
              </div>
              <button
                type="button"
                onClick={onRemove}
                className="p-2 hover:bg-[#D9D9D6]  transition-colors"
                data-testid={`${testId}-remove`}
              >
                <X className="w-5 h-5 text-[#888B8D]" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AppointmentPage = () => {
  const [formData, setFormData] = useState({
    customer_name: "",
    email: "",
    phone: "",
    service_type: "",
    vehicle_info: "",
    vin: "",
    vin_photo_base64: "",
    vin_photo_filename: "",
    mileage: "",
    mileage_photo_base64: "",
    mileage_photo_filename: "",
    vehicle_doc_base64: "",
    vehicle_doc_filename: "",
    preferred_date: null,
    preferred_time: "",
    message: "",
    license_plate: ""
  });
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  // Tire storage lookup
  const [licensePlate, setLicensePlate] = useState("");
  const [storedTires, setStoredTires] = useState([]);
  const [tiresLoading, setTiresLoading] = useState(false);
  const [tilesMap, setTilesMap] = useState({});
  
  // File states
  const [vinFile, setVinFile] = useState(null);
  const [vinPreview, setVinPreview] = useState(null);
  const [mileageFile, setMileageFile] = useState(null);
  const [mileagePreview, setMileagePreview] = useState(null);
  const [docFile, setDocFile] = useState(null);
  const [docPreview, setDocPreview] = useState(null);
  
  // Refs
  const vinInputRef = useRef(null);
  const mileageInputRef = useRef(null);
  const docInputRef = useRef(null);

  const serviceTypes = [
    { value: "inspektion", label: "Inspektion & Wartung", icon: <Wrench className="w-5 h-5" /> },
    { value: "reparatur", label: "Reparatur", icon: <Car className="w-5 h-5" /> },
    { value: "tuev", label: "TÜV / HU", icon: <Shield className="w-5 h-5" /> },
    { value: "klima", label: "Klimaservice", icon: <Snowflake className="w-5 h-5" /> },
    { value: "reifen", label: "Reifenservice", icon: <CircleDot className="w-5 h-5" /> },
    { value: "sonstiges", label: "Sonstiges", icon: <Wrench className="w-5 h-5" /> }
  ];

  // Load tiles for tire location display
  useEffect(() => {
    const loadTiles = async () => {
      try {
        const res = await axios.get(`${API}/tiremap/tiles`);
        const map = {};
        res.data.forEach(t => { map[t.id] = t; });
        setTilesMap(map);
      } catch (e) {
        // Tiles not available, ignore
      }
    };
    loadTiles();
  }, []);

  // Search for stored tires when license plate changes (for tire service)
  const searchStoredTires = async () => {
    if (!licensePlate || licensePlate.length < 3) {
      setStoredTires([]);
      return;
    }
    
    setTiresLoading(true);
    try {
      const res = await axios.get(`${API}/tiremap/tires/by-plate/${licensePlate}`);
      setStoredTires(res.data);
      if (res.data.length > 0) {
        toast.success(`${res.data.length} Reifen im Lager gefunden!`);
      }
    } catch (error) {
      console.error("Error searching tires:", error);
    } finally {
      setTiresLoading(false);
    }
  };

  const timeSlots = [
    "08:00", "08:30", "09:00", "09:30", "10:00", "10:30",
    "11:00", "11:30", "12:00", "13:00", "13:30", "14:00",
    "14:30", "15:00", "15:30", "16:00", "16:30", "17:00"
  ];

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handlePhotoCapture = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 10MB for photos)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Datei zu groß. Maximal 10MB erlaubt.");
      return;
    }

    try {
      const base64 = await convertToBase64(file);
      const previewUrl = URL.createObjectURL(file);
      
      if (type === 'vin') {
        setVinFile(file);
        setVinPreview(previewUrl);
        setFormData(prev => ({
          ...prev,
          vin_photo_base64: base64,
          vin_photo_filename: file.name
        }));
      } else if (type === 'mileage') {
        setMileageFile(file);
        setMileagePreview(previewUrl);
        setFormData(prev => ({
          ...prev,
          mileage_photo_base64: base64,
          mileage_photo_filename: file.name
        }));
      } else if (type === 'doc') {
        setDocFile(file);
        setDocPreview(file.type.startsWith('image/') ? previewUrl : null);
        setFormData(prev => ({
          ...prev,
          vehicle_doc_base64: base64,
          vehicle_doc_filename: file.name
        }));
      }
      
      toast.success("Foto erfolgreich aufgenommen");
    } catch (error) {
      toast.error("Fehler beim Verarbeiten des Fotos");
    }
  };

  const removePhoto = (type) => {
    if (type === 'vin') {
      setVinFile(null);
      setVinPreview(null);
      setFormData(prev => ({ ...prev, vin_photo_base64: "", vin_photo_filename: "" }));
      if (vinInputRef.current) vinInputRef.current.value = "";
    } else if (type === 'mileage') {
      setMileageFile(null);
      setMileagePreview(null);
      setFormData(prev => ({ ...prev, mileage_photo_base64: "", mileage_photo_filename: "" }));
      if (mileageInputRef.current) mileageInputRef.current.value = "";
    } else if (type === 'doc') {
      setDocFile(null);
      setDocPreview(null);
      setFormData(prev => ({ ...prev, vehicle_doc_base64: "", vehicle_doc_filename: "" }));
      if (docInputRef.current) docInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.preferred_date) {
      toast.error("Bitte wählen Sie ein Datum aus");
      return;
    }

    if (!formData.service_type) {
      toast.error("Bitte wählen Sie einen Service aus");
      return;
    }

    if (!formData.preferred_time) {
      toast.error("Bitte wählen Sie eine Uhrzeit aus");
      return;
    }

    setLoading(true);
    
    try {
      // Build tire info message if tires were found
      let tireInfoMessage = "";
      if (formData.service_type === "reifen" && storedTires.length > 0) {
        tireInfoMessage = "\n\n━━━━━━━━━━━━━━━━━━━━━━━━\n";
        tireInfoMessage += "REIFEN IM LAGER\n";
        tireInfoMessage += `Kennzeichen: ${licensePlate}\n`;
        tireInfoMessage += "━━━━━━━━━━━━━━━━━━━━━━━━\n\n";
        storedTires.forEach((tire, idx) => {
          const location = tilesMap[tire.tile_id]?.name || "Unbekannt";
          const tireType = tire.is_winter ? "Winterreifen" : "Sommerreifen";
          const quantity = tire.quantity || 1;
          
          tireInfoMessage += `Reifensatz ${idx + 1}:\n`;
          tireInfoMessage += `  Anzahl: ${quantity}x\n`;
          tireInfoMessage += `  Größe: ${tire.size}\n`;
          tireInfoMessage += `  Marke: ${tire.brand}\n`;
          tireInfoMessage += `  Typ: ${tireType}\n`;
          
          // Show individual profile depths if available
          if (tire.profile_depths && tire.profile_depths.length > 0) {
            tireInfoMessage += `  Profiltiefen: ${tire.profile_depths.map((d, i) => `R${i+1}: ${d}mm`).join(", ")}\n`;
          } else if (tire.profile_depth) {
            tireInfoMessage += `  Profiltiefe: ${tire.profile_depth}mm\n`;
          }
          
          tireInfoMessage += `  Lagerort: ${location}\n\n`;
        });
      }

      const submitData = {
        ...formData,
        license_plate: licensePlate,
        preferred_date: format(formData.preferred_date, "yyyy-MM-dd"),
        message: formData.message + tireInfoMessage
      };
      
      await axios.post(`${API}/appointments`, submitData);
      setSuccess(true);
      toast.success("Terminanfrage erfolgreich gesendet!");
    } catch (error) {
      console.error("Error submitting appointment:", error);
      toast.error("Fehler beim Senden der Anfrage. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = () => {
    setSuccess(false);
    setFormData({
      customer_name: "",
      email: "",
      phone: "",
      service_type: "",
      vehicle_info: "",
      vin: "",
      vin_photo_base64: "",
      vin_photo_filename: "",
      mileage: "",
      mileage_photo_base64: "",
      mileage_photo_filename: "",
      vehicle_doc_base64: "",
      vehicle_doc_filename: "",
      preferred_date: null,
      preferred_time: "",
      message: ""
    });
    setVinFile(null);
    setVinPreview(null);
    setMileageFile(null);
    setMileagePreview(null);
    setDocFile(null);
    setDocPreview(null);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <div className="w-20 h-20 bg-green-100  flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-black  mb-4">
            Vielen Dank für Ihre Anfrage!
          </h1>
          <p className="text-[#888B8D] mb-8">
            Wir haben Ihre Terminanfrage erhalten und werden uns schnellstmöglich bei Ihnen melden, 
            um den Termin zu bestätigen.
          </p>
          <Button 
            onClick={resetForm}
            className="bg-[#000000] text-white hover:bg-[#1a1a1a"
            data-testid="book-another-btn"
          >
            Weiteren Termin buchen
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="bg-[#000000] py-16" data-testid="appointment-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white  normal-case tracking-tight mb-4">
            Termin buchen
          </h1>
          <div className="h-1 w-16 bg-[#EFDF00] mb-6" />
          <p className="text-[#888B8D] max-w-2xl">
            Vereinbaren Sie einen Werkstatttermin online. Wir melden uns zeitnah bei Ihnen 
            zur Bestätigung.
          </p>
        </div>
      </section>

      {/* Booking Form */}
      <section className="py-16" data-testid="appointment-form-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Service Selection */}
            <div className="lg:col-span-1">
              <h3 className="text-lg font-bold text-black  mb-4">
                Unsere Services
              </h3>
              <div className="space-y-3">
                {serviceTypes.map((service) => (
                  <button
                    key={service.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, service_type: service.value }))}
                    data-testid={`service-${service.value}`}
                    className={cn(
                      "w-full flex items-center gap-3 p-4  border transition-all text-left",
                      formData.service_type === service.value
                        ? "bg-[#000000] text-white border-[#000000]"
                        : "bg-white text-[#888B8D] border-[#D9D9D6] hover:border-[#000000]"
                    )}
                  >
                    <div className={cn(
                      "p-2 ",
                      formData.service_type === service.value
                        ? "bg-[#EFDF00] text-black"
                        : "bg-[#EBEBEB]"
                    )}>
                      {service.icon}
                    </div>
                    <span className="font-medium">{service.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Form */}
            <Card className="lg:col-span-2 border-0 shadow-none">
              <CardHeader>
                <CardTitle className=" text-black">
                  Ihre Daten
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Personal Data */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="customer_name" className="text-[#333333]">Name *</Label>
                      <Input
                        id="customer_name"
                        name="customer_name"
                        value={formData.customer_name}
                        onChange={handleChange}
                        required
                        placeholder="Max Mustermann"
                        className="mt-1"
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-[#333333]">E-Mail *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="max@beispiel.de"
                        className="mt-1"
                        data-testid="input-email"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-[#333333]">Telefon *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="+49 123 456789"
                      className="mt-1"
                      data-testid="input-phone"
                    />
                  </div>

                  {/* Vehicle Data Section */}
                  <div className="border-t pt-6">
                    <h4 className="font-bold text-black mb-4 flex items-center gap-2">
                      <Car className="w-5 h-5" />
                      Fahrzeugdaten
                    </h4>
                    
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="vehicle_info" className="text-[#333333]">Fahrzeug *</Label>
                        <Input
                          id="vehicle_info"
                          name="vehicle_info"
                          value={formData.vehicle_info}
                          onChange={handleChange}
                          required
                          placeholder="z.B. Renault Clio, 2020"
                          className="mt-1"
                          data-testid="input-vehicle"
                        />
                      </div>

                      {/* VIN with Photo */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="vin" className="text-[#333333]">Fahrgestellnummer (VIN) *</Label>
                          <Input
                            id="vin"
                            name="vin"
                            value={formData.vin}
                            onChange={handleChange}
                            required
                            placeholder="z.B. VF1RFB00123456789"
                            className="mt-1"
                            data-testid="input-vin"
                          />
                        </div>
                        <PhotoUpload
                          label="VIN Foto"
                          description="Fotografieren Sie das VIN-Schild"
                          file={vinFile}
                          preview={vinPreview}
                          onCapture={(e) => handlePhotoCapture(e, 'vin')}
                          onRemove={() => removePhoto('vin')}
                          inputRef={vinInputRef}
                          testId="vin-photo"
                        />
                      </div>

                      {/* Mileage with Photo */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="mileage" className="text-[#333333]">Kilometerstand *</Label>
                          <Input
                            id="mileage"
                            name="mileage"
                            value={formData.mileage}
                            onChange={handleChange}
                            required
                            placeholder="z.B. 45000"
                            className="mt-1"
                            data-testid="input-mileage"
                          />
                        </div>
                        <PhotoUpload
                          label="Tacho Foto"
                          description="Fotografieren Sie den Kilometerstand"
                          file={mileageFile}
                          preview={mileagePreview}
                          onCapture={(e) => handlePhotoCapture(e, 'mileage')}
                          onRemove={() => removePhoto('mileage')}
                          inputRef={mileageInputRef}
                          testId="mileage-photo"
                        />
                      </div>

                      {/* Vehicle Document Photo */}
                      <PhotoUpload
                        label="Fahrzeugschein"
                        description="Fotografieren Sie Ihren Fahrzeugschein"
                        file={docFile}
                        preview={docPreview}
                        onCapture={(e) => handlePhotoCapture(e, 'doc')}
                        onRemove={() => removePhoto('doc')}
                        inputRef={docInputRef}
                        testId="doc-photo"
                      />
                    </div>
                  </div>

                  {/* Tire Service Section - only show when tire service is selected */}
                  {formData.service_type === "reifen" && (
                    <div className="border-t pt-6">
                      <h4 className="font-bold text-black mb-4 flex items-center gap-2">
                        <CircleDot className="w-5 h-5" />
                        Reifenservice - Lagersuche
                      </h4>
                      
                      <div className="bg-blue-50 border border-blue-200 p-4 mb-4">
                        <div className="flex items-start gap-3">
                          <Package className="w-5 h-5 text-blue-600 mt-0.5" />
                          <div>
                            <p className="text-sm text-blue-800 font-medium">
                              Haben Sie Reifen bei uns eingelagert?
                            </p>
                            <p className="text-xs text-blue-600 mt-1">
                              Geben Sie Ihr Kennzeichen ein, um Ihre eingelagerten Reifen zu finden.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Input
                          value={licensePlate}
                          onChange={(e) => setLicensePlate(e.target.value.toUpperCase())}
                          placeholder="z.B. B-TH 1234"
                          className="uppercase"
                          onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), searchStoredTires())}
                        />
                        <Button 
                          type="button"
                          onClick={searchStoredTires}
                          disabled={tiresLoading || licensePlate.length < 3}
                          className="bg-black text-white"
                        >
                          {tiresLoading ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent animate-spin" />
                          ) : (
                            <Search className="w-4 h-4" />
                          )}
                        </Button>
                      </div>

                      {/* Display found tires */}
                      {storedTires.length > 0 && (
                        <div className="mt-4 space-y-2">
                          <p className="text-sm font-medium text-green-700 flex items-center gap-2">
                            <CheckCircle className="w-4 h-4" />
                            {storedTires.length} Reifen im Lager gefunden:
                          </p>
                          <div className="bg-green-50 border border-green-200 p-3 space-y-2">
                            {storedTires.map((tire, idx) => (
                              <div key={tire.id || idx} className="flex items-center gap-3 bg-white p-2 rounded">
                                {tire.is_winter ? (
                                  <Snowflake className="w-5 h-5 text-blue-500" />
                                ) : (
                                  <Sun className="w-5 h-5 text-yellow-500" />
                                )}
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <p className="font-medium text-sm">{tire.size} - {tire.brand}</p>
                                    <Badge className="bg-[#EFDF00] text-black text-xs">{tire.quantity || 1}x</Badge>
                                  </div>
                                  <p className="text-xs text-gray-500">
                                    {tire.profile_depths && tire.profile_depths.length > 0 
                                      ? `Profiltiefen: ${tire.profile_depths.map((d, i) => `R${i+1}: ${d}mm`).join(", ")}`
                                      : `Profil: ${tire.profile_depth || "N/A"}mm`
                                    } • 
                                    Lagerort: <span className="font-medium">{tilesMap[tire.tile_id]?.name || "Unbekannt"}</span>
                                  </p>
                                </div>
                                <Badge className={tire.is_winter ? "bg-blue-100 text-blue-800" : "bg-yellow-100 text-yellow-800"}>
                                  {tire.is_winter ? "Winter" : "Sommer"}
                                </Badge>
                              </div>
                            ))}
                          </div>
                          <p className="text-xs text-gray-500">
                            Diese Information wird automatisch an unsere Werkstatt übermittelt.
                          </p>
                        </div>
                      )}

                      {licensePlate.length >= 3 && storedTires.length === 0 && !tiresLoading && (
                        <div className="mt-4 flex items-center gap-2 text-amber-600 text-sm">
                          <AlertCircle className="w-4 h-4" />
                          Keine eingelagerten Reifen für dieses Kennzeichen gefunden.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Date/Time Section */}
                  <div className="border-t pt-6">
                    <h4 className="font-bold text-black mb-4 flex items-center gap-2">
                      <CalendarIcon className="w-5 h-5" />
                      Wunschtermin
                    </h4>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-[#333333]">Datum *</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full justify-start text-left font-normal mt-1",
                                !formData.preferred_date && "text-muted-foreground"
                              )}
                              data-testid="date-picker-trigger"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {formData.preferred_date ? (
                                format(formData.preferred_date, "PPP", { locale: de })
                              ) : (
                                <span>Datum wählen</span>
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={formData.preferred_date}
                              onSelect={(date) => setFormData(prev => ({ ...prev, preferred_date: date }))}
                              disabled={(date) => date < new Date() || date.getDay() === 0}
                              initialFocus
                              data-testid="calendar"
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                      <div>
                        <Label className="text-[#333333]">Uhrzeit *</Label>
                        <Select
                          value={formData.preferred_time}
                          onValueChange={(value) => setFormData(prev => ({ ...prev, preferred_time: value }))}
                        >
                          <SelectTrigger className="mt-1" data-testid="time-select">
                            <SelectValue placeholder="Uhrzeit wählen" />
                          </SelectTrigger>
                          <SelectContent>
                            {timeSlots.map((time) => (
                              <SelectItem key={time} value={time}>
                                <div className="flex items-center gap-2">
                                  <Clock className="w-4 h-4" />
                                  {time} Uhr
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-[#333333]">Nachricht (optional)</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Beschreiben Sie Ihr Anliegen..."
                      rows={4}
                      className="mt-1"
                      data-testid="input-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || !formData.service_type || !formData.preferred_time || !formData.preferred_date || !formData.vin || !formData.mileage || !formData.vehicle_info}
                    className="w-full bg-[#EFDF00] text-black hover:bg-[#EFDF00 font-bold py-6 text-lg"
                    data-testid="submit-appointment-btn"
                  >
                    {loading ? "Wird gesendet..." : "Termin anfragen"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-white" data-testid="appointment-info">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="w-14 h-14 bg-[#EBEBEB]  flex items-center justify-center mx-auto mb-4">
                <CalendarIcon className="w-7 h-7 text-black" />
              </div>
              <h4 className="font-bold text-black mb-2">Flexible Termine</h4>
              <p className="text-[#888B8D] text-sm">
                Montag bis Freitag 8-18 Uhr, Samstag 9-14 Uhr
              </p>
            </div>
            <div>
              <div className="w-14 h-14 bg-[#EBEBEB]  flex items-center justify-center mx-auto mb-4">
                <Clock className="w-7 h-7 text-black" />
              </div>
              <h4 className="font-bold text-black mb-2">Schnelle Bestätigung</h4>
              <p className="text-[#888B8D] text-sm">
                Wir melden uns innerhalb von 24 Stunden
              </p>
            </div>
            <div>
              <div className="w-14 h-14 bg-[#EBEBEB]  flex items-center justify-center mx-auto mb-4">
                <Camera className="w-7 h-7 text-black" />
              </div>
              <h4 className="font-bold text-black mb-2">Foto-Upload</h4>
              <p className="text-[#888B8D] text-sm">
                Laden Sie Fotos direkt mit der Kamera hoch
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AppointmentPage;
